/**
 * Shared routing + copy for the two authored-document kinds (compliance manual
 * and Form ADV Part 2A brochure). Both render the same parameterized pages
 * (manual.tsx / manual-section.tsx / manual-package.tsx); these typed Link
 * helpers and the DOC_UI copy map are the only doc-type-specific surface, so the
 * substrate stays DRY (ADR-0007).
 */
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { DocumentType } from "@/lib/api/manuals";

export interface DocUiConfig {
  /** Page title on the overview. */
  title: string;
  /** Overview subtitle. */
  subtitle: string;
  /** Build/intake screen title (no document yet). */
  buildTitle: string;
  buildSubtitle: string;
  createLabel: string;
  packageLabel: string;
  /** Lowercase noun for inline copy ("manual" / "brochure"). */
  noun: string;
  emptyTitle: string;
  emptyDescription: string;
}

export const DOC_UI: Record<DocumentType, DocUiConfig> = {
  compliance_manual: {
    title: "Compliance manual",
    subtitle:
      "Argus tailors a Rule 206(4)-7 policies-and-procedures manual to your firm — every section is yours to review.",
    buildTitle: "Build your compliance manual",
    buildSubtitle:
      "Argus tailors a Rule 206(4)-7 policies-and-procedures manual to your firm. Confirm a few inputs — you can change them anytime.",
    createLabel: "Create & tailor manual",
    packageLabel: "Manual package",
    noun: "manual",
    emptyTitle: "No compliance manual yet",
    emptyDescription:
      "Your firm has not built its compliance manual in Argus. Ask an owner or admin to start it.",
  },
  adv_2a_brochure: {
    title: "Form ADV Part 2A brochure",
    subtitle:
      "Argus drafts your Part 2A client brochure from the same firm profile, kept aligned with your compliance manual.",
    buildTitle: "Build your Form ADV Part 2A brochure",
    buildSubtitle:
      "Argus tailors the 18-item Part 2A brochure to your firm and keeps it aligned with your compliance manual. Confirm a few inputs — you can change them anytime.",
    createLabel: "Create & tailor brochure",
    packageLabel: "Brochure package",
    noun: "brochure",
    emptyTitle: "No Form ADV Part 2A brochure yet",
    emptyDescription:
      "Your firm has not built its Part 2A brochure in Argus. Ask an owner or admin to start it.",
  },
};

const isBrochure = (d: DocumentType) => d === "adv_2a_brochure";

/** Link to a document's overview. */
export function DocHomeLink({
  docType,
  slug,
  className,
  children,
}: {
  docType: DocumentType;
  slug: string;
  className?: string;
  children: ReactNode;
}) {
  return isBrochure(docType) ? (
    <Link to="/t/$slug/brochure" params={{ slug }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/t/$slug/manual" params={{ slug }} className={className}>
      {children}
    </Link>
  );
}

/** Link to a single section/item editor. */
export function DocSectionLink({
  docType,
  slug,
  sectionKey,
  className,
  children,
}: {
  docType: DocumentType;
  slug: string;
  sectionKey: string;
  className?: string;
  children: ReactNode;
}) {
  return isBrochure(docType) ? (
    <Link to="/t/$slug/brochure/$sectionKey" params={{ slug, sectionKey }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/t/$slug/manual/$sectionKey" params={{ slug, sectionKey }} className={className}>
      {children}
    </Link>
  );
}

/** Link to the print-clean package. */
export function DocPackageLink({
  docType,
  slug,
  className,
  children,
}: {
  docType: DocumentType;
  slug: string;
  className?: string;
  children: ReactNode;
}) {
  return isBrochure(docType) ? (
    <Link to="/t/$slug/brochure/package" params={{ slug }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/t/$slug/manual/package" params={{ slug }} className={className}>
      {children}
    </Link>
  );
}
